import { cloneElement, ComponentChild, ComponentChildren } from 'preact';
import useFocusTrap from '@charlietango/use-focus-trap';
import clsx from 'clsx';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useRef } from 'react';
import { useUniqueId } from 'utilities/use-unique-id';

import { useModalTracker } from 'components/ModalsTrackerProvider';
import { Portal } from 'components/Portal';

import { Header } from './components/Header';

import styles from './Modal.module.scss';

interface ModalProps {
  open?: boolean;
  title?: string;
  activator?: JSX.Element;
  large?: boolean;
  condensed?: boolean;
  priority?: 0 | 1;
  children: ComponentChildren;
  onClose?: () => void;
  classNames?: string[];
}

export function Modal({
  open: openProp = false,
  title,
  activator,
  large,
  condensed,
  children,
  onClose,
  priority = 0,
  classNames = [],
}: ModalProps) {
  const [open, setOpen] = useState(openProp);
  const dialogRef = useFocusTrap(open);
  const headerId = useUniqueId('ModalHeader');
  const modalId = useUniqueId('Modal');
  const className = clsx(
    styles.Modal,
    condensed && styles.condensed,
    priority === 1 && styles.topmost,
    ...classNames,
  );

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => {
    setOpen(false);
    onClose && onClose();
  }, [onClose]);

  useEffect(() => {
    setOpen(openProp);
  }, [openProp]);

  const { addModal, removeModal } = useModalTracker();

  const removeModalRef = useRef(removeModal);
  const addModalRef = useRef(addModal);
  const modalIdRef = useRef(modalId);
  const priorityRef = useRef(priority);
  const handleCloseRef = useRef(handleClose);

  removeModalRef.current = removeModal;
  addModalRef.current = addModal;
  priorityRef.current = priority;
  handleCloseRef.current = handleClose;

  useEffect(() => {
    const removeModal = removeModalRef.current;
    const addModal = addModalRef.current;
    const modalId = modalIdRef.current;
    const priority = priorityRef.current;
    const handleClose = handleCloseRef.current;

    if (open) {
      addModal({
        id: modalId,
        priority,
        closeModal: handleClose,
      });
    } else {
      removeModal(modalId);
    }
  }, [open]);

  useEffect(() => {
    const modalId = modalIdRef.current;

    return () => {
      const removeModal = removeModalRef.current;

      removeModal(modalId);
    };
  }, []);

  const activatorMarkup = activator
    ? cloneElement(activator, { ...activator.props, onClick: handleOpen })
    : null;

  let modalMarkup: ComponentChild;

  if (open) {
    modalMarkup = (
      <div className={className}>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={headerId}
          tabIndex={-1}
          className={clsx(styles.Dialog, large && styles['Dialog-large'])}
        >
          <div className={styles.ModalContent}>
            <Header title={title} id={headerId} onClose={handleClose} />
            <div className={styles.Body}>{children}</div>
          </div>
        </div>
        <div className={styles.Backdrop} onClick={handleClose}>
          &nbsp;
        </div>
      </div>
    );
  }

  return (
    <>
      {activatorMarkup}
      <Portal>{modalMarkup}</Portal>
    </>
  );
}
